/**
 * Шапка 56px и каркас приложения — docs/ui-design.md §5.1.
 *
 * ПОРЯДОК ОРГАНОВ СПРАВА ОСМЫСЛЕН, а не «как поместилось»: режим публикаций → модель
 * писателя → ход прогона → «Запустить генерацию» → профиль. Слева направо это «что с
 * системой» → «что я делаю» → «кто я»: первые два говорят о состоянии всего канала и потому
 * стоят дальше от края, где живёт личное; действие примыкает к имени, как его и просили.
 * Разделитель между ними нужен по существу: и включённый режим, и главная кнопка фиолетовые,
 * и без границы они читались бы одним органом.
 *
 * МОДЕЛЬ ПИСАТЕЛЯ СТОИТ ПО ЛЕВУЮ СТОРОНУ РАЗДЕЛИТЕЛЯ, рядом с режимом публикаций, а не в
 * диалоге запуска, где она жила раньше. Там она правила РОВНО ОДИН прогон — тот, который
 * человек в этот момент запускал, — а темы расписания и переделки шли моделью конфига
 * бренда. Справа от разделителя она читалась бы как параметр кнопки, то есть ровно тем, чем
 * была и что оказалось неверно понято. С 19.09.2026 этот орган — «Настройки»: модель
 * писателя и стиль по умолчанию одной подписью (`WriterModelSwitch.tsx`); обе величины шапка
 * грузит сама и сообщает экрану под собой об их смене (`shellSettings.ts`).
 *
 * ХОД ПРОГОНА СТОИТ ВПЛОТНУЮ СЛЕВА ОТ КНОПКИ — по ту же сторону разделителя, что и она:
 * это последствие нажатия, а не состояние канала. Пока прогона нет, органа нет вовсе
 * (`RunStatusBar` возвращает null), и шапка выглядит ровно как прежде.
 *
 * ПРИНЯТАЯ ЗАЯВКА ЕДЕТ ИЗ ДИАЛОГА В БАР ЧЕРЕЗ ШАПКУ, а не запрашивается баром заново: POST
 * возвращает саму заявку, и без этой передачи бар узнал бы о нажатии человека только
 * следующим опросом очереди — то есть с задержкой ровно там, где её быть не должно. Живёт
 * этот снимок РОВНО до первого ответа очереди, в котором заявка есть (`onAcceptedSeen`):
 * дальше о ней рассказывает список, а несброшенный снимок через шесть заявок выпал бы из
 * окна списка и навсегда подвесил бы в шапке «в очереди» с секундным опросом. Снимок чужого
 * бренда бар игнорирует сам (он несёт свой `brand`), поэтому гасить его при переходе между
 * каналами шапке не нужно.
 *
 * БРЕНДЫ ПЕРЕКЛЮЧАЕТ ЗНАК (docs/multi-brand.md §6, 14.10.2026). Вкладок брендов нет и места под
 * них не держится: знак слева стал кнопкой (`BrandSwitcher.tsx`), меню показывает знаки всех
 * брендов. Ширины шапке он не прибавляет — лестница подписей перемерена с ним.
 *
 * АВТООДОБРЕНИЕ (docs/ai-lab.md, 2026-10-25) — орган сразу за режимом публикаций, по ту же
 * сторону разделителя: это тоже «куда и как уходят посты канала». Есть только у бренда, чей
 * конфиг автоодобрение разрешает (`autoApprovalAllowed`); у остальных брендов шапка ровно
 * прежняя и состояние выключателя не запрашивается. Переключение растит `rev`, как смена
 * режима: выключение возвращает посты из слотов на согласование, и экран под шапкой
 * перечитывает бэклог, календарь и открытый черновик (`ShellRevWatcher`, `DraftPanel`).
 *
 * СОСТОЯНИЕ РЕЖИМА ГРУЗИТСЯ ЗДЕСЬ, а не внутри переключателя: тем же ответом красится нижняя
 * граница шапки, и второй запрос ради одной величины разошёлся бы с первым. Режим у каждого
 * бренда свой (docs/multi-brand.md §3): запрос несёт бренд адреса, и красная граница шапки
 * говорит о канале ЭТОГО бренда — у соседнего режим может быть другим.
 *
 * ПОЛОСА ПРИЛОЖЕНИЯ ОГРАНИЧЕНА, А ШАПКА — НЕТ. Ограничивать `.hdr` целиком нельзя: его
 * нижняя граница красится в красный на паузе и обязана идти от края до края окна, иначе
 * периферийный признак «публикации выключены» превращается в короткую чёрточку посередине
 * 4K-монитора. Поэтому полосой во всю ширину остаётся `.hdr` (фон + граница), а `flex`,
 * `gap` и поля переехали внутрь на `.hdrInner` с тем же `max-width: var(--shell-max)` и
 * `margin-inline: auto`, что у `.body`. Так органы шапки стоят ровно над содержимым, а не
 * разъезжаются с ним на широком экране.
 *
 * КЛАССЫ `paused` И `frame` ОСТАЮТСЯ НА ВНЕШНЕМ `.hdr`: `Shell.module.css` селектит их как
 * `:global(.hdr).paused` (base.css подключается ПОСЛЕ модулей и при равной специфичности
 * выиграл бы он), и `Shell.test.tsx` ищет полосу по классу `.hdr`, проверяя на ней `paused`.
 *
 * `100dvh`, А НЕ `100vh`: на мобильных браузерах `vh` считается по окну БЕЗ схлопывающейся
 * адресной строки, поэтому каркас на телефоне оказывался на 60–120px выше экрана и нижний
 * край страницы был недостижим без прокрутки, которой там быть не должно.
 */

import { type JSX, type ReactNode, useCallback, useMemo, useState } from 'react';
import { Link } from 'wouter';
import type { GenerationRequest, PublicationDispatch, WriterModelOverride } from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import {
  agentsOffReason,
  autoApprovalAllowed,
  brandTitle,
  offLabel,
  useBrands,
} from '../brand.tsx';
import { useSession } from '../session.tsx';
import { AutoApprovalSwitch, autoApprovalOf } from './AutoApprovalSwitch.tsx';
import { BrandSwitcher } from './BrandSwitcher.tsx';
import { initials } from './format.ts';
import { GenerationDialog } from './GenerationDialog.tsx';
import { IconChevronDown, IconPlus } from './icons.tsx';
import { ModeSwitch } from './ModeSwitch.tsx';
import { MonitoringRunButton } from './MonitoringRunButton.tsx';
import { ProfileDialog } from './ProfileDialog.tsx';
import { RunStatusBar } from './RunStatusBar.tsx';
import styles from './Shell.module.css';
import { type ShellSettings, ShellSettingsContext } from './shellSettings.ts';
import { WriterModelSwitch } from './WriterModelSwitch.tsx';

export type TabId = 'backlog' | 'plan' | 'monitoring';

const TABS: readonly { readonly id: TabId; readonly label: string; readonly path: string }[] = [
  { id: 'backlog', label: 'Бэклог', path: 'backlog' },
  { id: 'plan', label: 'Контент-план', path: 'plan' },
  { id: 'monitoring', label: 'Мониторинг', path: 'monitoring' },
];

type OpenDialog = 'generation' | 'profile' | null;

export function Shell({
  brand,
  active,
  children,
}: {
  brand: string;
  /**
   * `null` — экран не вкладка шапки (редактор стилей, сравнение стилей): туда ведут из
   * «Настроек» и из заявки, и подсвеченная чужая вкладка врала бы о том, где человек.
   */
  active: TabId | null;
  children: ReactNode;
}): JSX.Element {
  const { user } = useSession();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [accepted, setAccepted] = useState<GenerationRequest | null>(null);
  const brands = useBrands();
  // Режим публикаций и модель писателя — на бренд, поэтому оба запроса несут бренд. Грузятся
  // здесь, а не внутри своих органов: шапка и так держит состояние, а второй слой запросов
  // внутри компонентов разошёлся бы с первым.
  const dispatch = useResource<PublicationDispatch>(
    `/api/v1/publication-dispatch?brand=${encodeURIComponent(brand)}`,
  );
  const writerModel = useResource<WriterModelOverride>(
    `/api/v1/writer-model?brand=${encodeURIComponent(brand)}`,
  );
  // Стиль по умолчанию — вторая половина органа «Настройки» (docs/release-2026-09-19.md
  // §5.6). Ответ не типизируется здесь: его разбирает схемой сам орган.
  const styleDefault = useResource<unknown>(
    `/api/v1/style-default?brand=${encodeURIComponent(brand)}`,
  );
  // Третья половина «Настроек» — политика «все посты — расширенным сообщением»
  // (docs/release-2026-09-19.md §9.3). Разбирает её схемой сам орган, как стиль.
  const transportPolicy = useResource<unknown>(
    `/api/v1/transport-policy?brand=${encodeURIComponent(brand)}`,
  );
  // Настройки меняют два места — диалог шапки и редактор стилей под ней; оба перечитывают
  // шапку одной функцией, а экран узнаёт о смене из шапки счётчиком (`shellSettings.ts`).
  const [settingsRev, setSettingsRev] = useState(0);
  const { refetch: refetchWriterModel } = writerModel;
  const { refetch: refetchStyleDefault } = styleDefault;
  const { refetch: refetchTransportPolicy } = transportPolicy;
  const settingsChanged = useCallback((): void => {
    refetchWriterModel();
    refetchStyleDefault();
    refetchTransportPolicy();
    setSettingsRev((n) => n + 1);
  }, [refetchWriterModel, refetchStyleDefault, refetchTransportPolicy]);
  // Смена режима публикаций — тоже «настройки изменились» для экрана под шапкой: полоса решения
  // черновика показывает «Запостить в тестовый канал» только в боевом режиме и перечитывает
  // режим по тому же счётчику. Модель и стиль при этом не перечитываются — их не трогали.
  // Выключатель автоодобрения — только у бренда, которому его разрешает конфиг; у прочих
  // брендов состояние не спрашивается вовсе.
  const autoAllowed = autoApprovalAllowed(brands, brand);
  const autoApproval = useResource<unknown>(
    autoAllowed ? `/api/v1/auto-approval?brand=${encodeURIComponent(brand)}` : null,
  );
  const { refetch: refetchAutoApproval } = autoApproval;
  const autoApprovalOn = autoApprovalOf(autoApproval.data, brand)?.state === 'on';
  // Смена режима выключает автоодобрение бренда на бэкенде (docs/ai-lab.md §3.5): его
  // положение перечитывается вместе с режимом, иначе пилюля врала бы «вкл» до перезагрузки.
  const { refetch: refetchDispatch } = dispatch;
  const modeChanged = useCallback((): void => {
    refetchDispatch();
    refetchAutoApproval();
    setSettingsRev((n) => n + 1);
  }, [refetchDispatch, refetchAutoApproval]);
  const autoApprovalChanged = useCallback((): void => {
    refetchAutoApproval();
    setSettingsRev((n) => n + 1);
  }, [refetchAutoApproval]);
  const settings = useMemo<ShellSettings>(
    () => ({ rev: settingsRev, changed: settingsChanged }),
    [settingsRev, settingsChanged],
  );

  const testMode = dispatch.data?.state === 'test';
  // Бренд без сервисов (docs/multi-brand.md §8): кнопки остаются на своих местах и своей
  // ширины — порядок органов и лестница шапки держатся тестами и линейкой, — но гаснут с
  // причиной. Генерации нужен писатель; обход ей нужен не всегда (прогон по контексту),
  // поэтому кнопка гаснет только без писателя, а остальное скажет бэкенд.
  const monitoringOff = agentsOffReason(brands, brand, ['monitoring']);
  const generationOff = agentsOffReason(brands, brand, ['generation']);

  return (
    <div className="app" style={{ minHeight: '100dvh' }}>
      {/*
        Красная нижняя граница в тестовом режиме — второй, периферийный признак того же
        состояния. Переключатель отвечает на вопрос, когда на него смотрят; полоса видна и
        тогда, когда человек смотрит на пост, — а «одобренное уйдёт в тестовый канал, не
        подписчикам» он обязан помнить, одобряя. Имя класса (`paused`) осталось от рубильника.
      */}
      <div
        className={
          testMode ? `hdr ${styles['frame']} ${styles['paused']}` : `hdr ${styles['frame']}`
        }
      >
        <div className={styles['hdrInner']}>
          <BrandSwitcher brand={brand} textClassName={styles['wordmark']} />
          <div className={`tabs ${styles['tabs']}`}>
            {TABS.map((t) => (
              <Link
                key={t.id}
                href={`/b/${brand}/${t.path}`}
                className={t.id === active ? 'tab on' : 'tab'}
              >
                {t.label}
              </Link>
            ))}
          </div>
          <div className="hdr-sp" />

          <div className={styles['tools']}>
            <ModeSwitch
              brand={brand}
              brandTitle={brandTitle(brands, brand)}
              dispatch={dispatch.data}
              failed={dispatch.error !== undefined}
              canManage={user?.role === 'admin'}
              autoApprovalOn={autoAllowed && autoApprovalOn}
              onChanged={modeChanged}
            />
            {autoAllowed ? (
              <AutoApprovalSwitch
                brand={brand}
                brandTitle={brandTitle(brands, brand)}
                data={autoApproval.data}
                failed={autoApproval.error !== undefined}
                canManage={user?.role === 'admin'}
                mode={dispatch.data?.state}
                generationOff={generationOff}
                viewerLogin={user?.login ?? null}
                onChanged={autoApprovalChanged}
              />
            ) : null}
            <WriterModelSwitch
              brand={brand}
              generationOff={generationOff}
              model={writerModel.data}
              modelFailed={writerModel.error !== undefined}
              styleDefault={styleDefault.data}
              styleFailed={styleDefault.error !== undefined}
              transport={transportPolicy.data}
              transportFailed={transportPolicy.error !== undefined}
              onChanged={settingsChanged}
            />
            <span className={styles['sep']} aria-hidden="true" />
            <RunStatusBar
              brand={brand}
              accepted={accepted}
              onAcceptedSeen={() => {
                setAccepted(null);
              }}
            />
            {/*
              «Прогон мониторинга» — между ходом прогона и главной кнопкой (docs/release-
              2026-09-19.md §3.6): заказ поста из таблицы обхода — действие того же регистра,
              что «Запустить генерацию». Первая поставленная им заявка едет в полосу хода тем
              же путём, что заявка из диалога генерации.
            */}
            <MonitoringRunButton
              brand={brand}
              off={monitoringOff}
              onPicked={(request) => {
                setAccepted(request);
              }}
            />
            {/*
              Подпись задана и текстом, и aria-label: на узком окне видимая надпись
              укорачивается до «Генерация», а имя кнопки для клавиатуры и тестов обязано
              остаться одним и тем же независимо от ширины окна.

              Погашенная — `aria-disabled`, а не `disabled`: выключенная кнопка не получает ни
              фокуса, ни наведения, и причина в имени и подсказке была бы недостижима ровно
              там, где её ищут (тот же приём — `MonitoringRunDialog`, статус «тема»). Класс
              `btn-dis` вместо `btn-pri` меняет только цвета: поля и рамка у них одни.
            */}
            <button
              type="button"
              className={`btn ${generationOff === null ? 'btn-pri' : 'btn-dis'} ${styles['run']}`}
              aria-label={
                generationOff === null
                  ? 'Запустить генерацию'
                  : offLabel('Запустить генерацию', generationOff)
              }
              aria-disabled={generationOff === null ? undefined : true}
              title={
                generationOff === null ? undefined : offLabel('Запустить генерацию', generationOff)
              }
              onClick={() => {
                if (generationOff !== null) return;
                setDialog('generation');
              }}
            >
              <IconPlus />
              <span className={styles['runLong']}>Запустить генерацию</span>
              <span className={styles['runShort']}>Генерация</span>
            </button>
            <button
              type="button"
              className={`uch ${styles['profile']}`}
              aria-label="Аккаунт"
              onClick={() => {
                setDialog('profile');
              }}
            >
              <span className="ava">{user === null ? '—' : initials(user.display_name)}</span>
              <span className={styles['uname']}>{user?.display_name ?? ''}</span>
              <IconChevronDown />
            </button>
          </div>
        </div>
      </div>

      <ShellSettingsContext.Provider value={settings}>
        <div className={styles['body']}>{children}</div>
      </ShellSettingsContext.Provider>

      {dialog === 'generation' ? (
        <GenerationDialog
          brand={brand}
          onSubmitted={(request) => {
            setAccepted(request);
          }}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'profile' ? (
        <ProfileDialog
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </div>
  );
}
